import { create } from 'zustand';
import type { QuestionForClient, Difficulty, TitleDefinition } from '@beast-keeper/shared';
import { calculateQuestionScore, getTitleForScore } from '@beast-keeper/shared';

export interface GameState {
  questions: QuestionForClient[];
  currentIndex: number;
  score: number;
  streak: number;
  maxStreak: number;
  answers: number[];
  timeTakenMs: number[];
  isActive: boolean;
  isFinished: boolean;

  initGame: (questions: QuestionForClient[]) => void;
  answerQuestion: (answerIndex: number, timeMs: number) => {
    correct: boolean;
    score: number;
    newStreak: number;
  };
  nextQuestion: () => void;
  getResult: () => {
    score: number;
    title: TitleDefinition;
    maxStreak: number;
    answers: number[];
    timeTakenMs: number[];
  };
  reset: () => void;
}

export const useGameStore = create<GameState>((set, get) => ({
  questions: [],
  currentIndex: 0,
  score: 0,
  streak: 0,
  maxStreak: 0,
  answers: [],
  timeTakenMs: [],
  isActive: false,
  isFinished: false,

  initGame: (questions: QuestionForClient[]) => {
    set({
      questions,
      currentIndex: 0,
      score: 0,
      streak: 0,
      maxStreak: 0,
      answers: [],
      timeTakenMs: [],
      isActive: true,
      isFinished: false,
    });
  },

  answerQuestion: (answerIndex: number, timeMs: number) => {
    const { questions, currentIndex, streak } = get();
    const q = questions[currentIndex];
    const correct = answerIndex === q.correctIndex!;

    // We need correctIndex but QuestionForClient doesn't have it
    // The full question data comes from the API with correct answers
    // For now, the game logic uses the full data
    const timeLeft = Math.max(0, (20000 - timeMs) / 1000);
    const qScore = correct
      ? calculateQuestionScore((q as any).difficulty as Difficulty, timeLeft)
      : 0;
    const newStreak = correct ? streak + 1 : 0;
    const newMaxStreak = Math.max(get().maxStreak, newStreak);

    set((state) => ({
      score: state.score + qScore,
      streak: newStreak,
      maxStreak: newMaxStreak,
      answers: [...state.answers, answerIndex],
      timeTakenMs: [...state.timeTakenMs, timeMs],
    }));

    return { correct, score: qScore, newStreak };
  },

  nextQuestion: () => {
    const nextIdx = get().currentIndex + 1;
    if (nextIdx >= get().questions.length) {
      set({ isActive: false, isFinished: true });
    } else {
      set({ currentIndex: nextIdx });
    }
  },

  getResult: () => {
    const { score, maxStreak, answers, timeTakenMs } = get();
    return {
      score,
      title: getTitleForScore(score),
      maxStreak,
      answers,
      timeTakenMs,
    };
  },

  reset: () => {
    set({
      questions: [],
      currentIndex: 0,
      score: 0,
      streak: 0,
      maxStreak: 0,
      answers: [],
      timeTakenMs: [],
      isActive: false,
      isFinished: false,
    });
  },
}));
